import { type KeyboardEvent, useEffect, useRef, useState } from 'react';

interface EditableTitleProps {
  /** Current value. Empty string or null displays as `placeholder`. */
  value: string | null | undefined;
  placeholder: string;
  onCommit: (newValue: string) => void;
  editing: boolean;
  setEditing: (editing: boolean) => void;
  /** Class on the display span. */
  className?: string;
  /** Class on the input element. */
  inputClassName?: string;
  /** When true, a single click on the display span enters edit mode.
   * Otherwise edit mode is entered by double-click. */
  singleClick?: boolean;
  /** Optional aria-label for the input. */
  inputAriaLabel?: string;
  maxLength?: number;
}

/**
 * Inline-editable title used by the chat header and sidebar rows.
 *
 * Render contract:
 *   - `editing === false` → renders a `<span>` showing `value || placeholder`.
 *     Click/double-click on the span flips `editing` via `setEditing`.
 *   - `editing === true`  → renders an `<input>` seeded with `value`. Enter
 *     commits, Escape cancels, blur commits. Empty input on commit silently
 *     restores the original (no API call, no error state).
 *
 * The owning component controls `editing` so it can also flip it via external
 * affordances (pencil button on desktop, popover/sheet menu item).
 */
export function EditableTitle({
                                value,
                                placeholder,
                                onCommit,
                                editing,
                                setEditing,
                                className,
                                inputClassName,
                                singleClick = false,
                                inputAriaLabel,
                                maxLength = 60,
                              }: EditableTitleProps) {
  const [draft, setDraft] = useState<string>(value ?? '');
  const inputRef = useRef<HTMLInputElement | null>(null);

  // Seed and focus when entering edit mode. requestAnimationFrame keeps focus
  // after the input is in the DOM; .select() picks all text per spec.
  useEffect(() => {
    if (!editing) return;
    setDraft(value ?? '');
    const raf = requestAnimationFrame(() => {
      const el = inputRef.current;
      if (el) {
        el.focus();
        el.select();
      }
    });
    return () => cancelAnimationFrame(raf);
  }, [editing, value]);

  const commit = () => {
    const v = draft.trim();
    // Empty input → silently restore (no commit, no error state). Spec §"Shared Input Rules".
    if (v && v !== (value ?? '')) {
      onCommit(v);
    }
    setEditing(false);
  };

  const cancel = () => {
    setEditing(false);
  };

  if (!editing) {
    const display = value || placeholder;
    return (
      <span
        className={className}
        onClick={singleClick ? () => setEditing(true) : undefined}
        onDoubleClick={!singleClick ? () => setEditing(true) : undefined}
      >
        {display}
      </span>
    );
  }

  return (
    <input
      ref={inputRef}
      className={inputClassName}
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={(e: KeyboardEvent<HTMLInputElement>) => {
        // Prevent the row's onKeyDown (Enter→navigate) from firing.
        e.stopPropagation();
        // Ignore keys consumed by an IME (e.g. Enter/Escape in the 注音
        // candidate window on macOS); 229 covers Safari's late keydown.
        if (e.nativeEvent.isComposing || e.keyCode === 229) return;
        if (e.key === 'Enter') {
          e.preventDefault();
          commit();
        } else if (e.key === 'Escape') {
          e.preventDefault();
          cancel();
        }
      }}
      // Avoid row click/select while typing in the input.
      onClick={(e) => e.stopPropagation()}
      onDoubleClick={(e) => e.stopPropagation()}
      maxLength={maxLength}
      aria-label={inputAriaLabel}
    />
  );
}
